import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calculator, Paperclip, RefreshCw, Receipt } from "lucide-react";
import { fetchAdminFile, fetchAdminReceipt } from "@/lib/adminAuth";
import { FollowupDialog } from "@/components/admin/FollowupDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { auto: "Auto", moto: "Moto", rc_decennale: "RC Décennale", multirisque_pro: "Multirisque Pro", habitation: "Habitation", cybersecurite: "Cyber" };
const STATUS = { nouveau: "Coordonnées saisies", devis_calcule: "Tarif affiché", rappel: "Veut être rappelé", souscrire: "Veut souscrire", paye: "Payé" };
const statusLabel = (lead) => (lead.status === "devis_calcule" && lead.result?.rappel ? "Rappel conseiller" : STATUS[lead.status] || lead.status);
const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);

const LeadRow = ({ lead, onChange }) => {
  const r = lead.result;
  return (
    <div className="p-4 rounded-xl border border-slate-100 bg-white" data-testid={`calc-lead-${lead.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{LABELS[lead.product] || lead.product}</Badge>
          <Badge className="bg-[#102692]/10 text-[#102692]">{statusLabel(lead)}</Badge>
        </div>
        <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleString("fr-FR")}</span>
      </div>
      <p className="mt-2 text-sm font-medium text-slate-800">{[lead.contact?.prenom, lead.contact?.nom].filter(Boolean).join(" ")} · {lead.contact?.telephone} · {lead.contact?.email}</p>
      <p className="text-sm text-slate-600 mt-1" data-testid={`calc-lead-price-${lead.id}`}>
        {!r ? "Tarif non calculé" : r.rappel ? `Rappel conseiller — ${r.reason}` : `${eur(r.annual)}/an (${eur(r.monthly)}/mois) ${r.frais_inclus ? `dont ${eur(r.frais_inclus)} de frais inclus` : `+ ${eur(r.frais_dossier)} de frais`} · à la souscription ${eur(r.a_payer || 0)}`}
        {lead.status === "paye" && <span className="ml-2 font-semibold text-emerald-600" data-testid={`calc-lead-paid-${lead.id}`}>Payé {eur(lead.paid_amount || 0)}</span>}
      </p>
      {lead.prelevements_total > 0 && (
        <p className="text-xs text-slate-500 mt-1" data-testid={`calc-lead-installments-${lead.id}`}>
          Prélèvements mensuels : {lead.prelevements_payes || 0}/{lead.prelevements_total} payés · 1er le {new Date(lead.premier_prelevement).toLocaleDateString("fr-FR")}
        </p>
      )}
      {(lead.receipts || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {lead.receipts.map((rc) => (
            <Button key={rc.id} variant="ghost" size="sm" onClick={() => fetchAdminReceipt(rc.id)} data-testid={`calc-lead-receipt-${rc.id}`}>
              <Receipt className="w-4 h-4 mr-1" /> {rc.label}
            </Button>
          ))}
        </div>
      )}
      {lead.status !== "paye" && <FollowupDialog lead={lead} onSent={onChange} />}
      <details className="mt-2 text-xs text-slate-500">
        <summary className="cursor-pointer">Toutes les réponses</summary>
        <pre className="whitespace-pre-wrap mt-1">{JSON.stringify(lead.answers, null, 2)}</pre>
      </details>
      {lead.answers?.releve_file_id && (
        <Button variant="ghost" size="sm" className="mt-1" onClick={() => fetchAdminFile(lead.answers.releve_file_id)} data-testid={`calc-lead-file-${lead.id}`}>
          <Paperclip className="w-4 h-4 mr-1" /> {lead.answers.releve_filename}
        </Button>
      )}
    </div>
  );
};

export const CalcLeads = () => {
  const [leads, setLeads] = useState([]);
  const load = () => axios.get(`${API}/admin/leads`).then((r) => setLeads(r.data.leads)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <Card className="border-0 shadow-sm" data-testid="calc-leads-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-[#102692]" /> Leads des calculateurs</CardTitle>
          <CardDescription>Enregistrés dès la saisie du téléphone, envoyés à contact@jyvis-assurances.com</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="calc-leads-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {leads.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Aucun lead pour le moment</p>}
        {leads.map((l) => <LeadRow key={l.id} lead={l} onChange={load} />)}
      </CardContent>
    </Card>
  );
};
